import React from 'react'
import AdminNavbar from "../Navbars/AdminNavbar.js";
import Sidebar from "../Sidebar/Sidebar";
import { SuperAdminApis } from '../../apis/superAdminApi';
import { AxiosResponse } from 'axios';
import { useSelector } from 'react-redux';
import { Oval } from 'react-loader-spinner';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer, toast } from 'react-toastify';

export default function CalculateRate() {

  const [result, setResult] = React.useState<any>(null);
  const [countryTo, setCountryTo] = React.useState<any>([]);
  const [stateFrom, setStateFrom] = React.useState<any>([]);
  const [stateTo, setStateTo] = React.useState<any>([]);
  const [lgaFrom, setLgaFrom] = React.useState<any>([]);
  const [lgaTo, setLgaTo] = React.useState<any>([]);
  const [loader, setLoader] = React.useState<boolean>(false);

  const userLoginData = useSelector((state: any) => state.data.login.value);
  const isCreditCustomer = userLoginData?.data?.customer_type === 'credit';

  // Form data states
  const [countryToData, setCountryToData] = React.useState<any>('1'); // Default to Nigeria
  const [stateFromData, setStateFromData] = React.useState<any>('');
  const [stateToData, setStateToData] = React.useState<any>('');
  const [stateFromName, setStateFromName] = React.useState<string>('');
  const [stateToName, setStateToName] = React.useState<string>('');
  const [lgaFromData, setLgaFromData] = React.useState<any>('');
  const [lgaToData, setLgaToData] = React.useState<any>('');
  const [weight, setWeight] = React.useState<any>('');
  const [cubicWeight, setCubicWeight] = React.useState<any>('');

  // Cash customer service type
  const [serviceType, setServiceType] = React.useState<string>('domestic');

  // Credit customer fields
  const [shipmentRateType, setShipmentRateType] = React.useState<string>('fixed');
  const [specialUnitType, setSpecialUnitType] = React.useState<string>('');

  // For cubic weight calculation
  const [length, setLength] = React.useState<any>('');
  const [width, setWidth] = React.useState<any>('');
  const [height, setHeight] = React.useState<any>('');

  React.useEffect(() => {
    loadInitialData();
  }, []);

  // Auto-calculate cubic weight when dimensions change
  React.useEffect(() => {
    if (length && width && height) {
      const cubic = (parseFloat(length) * parseFloat(width) * parseFloat(height)) / 5000;
      setCubicWeight(cubic.toFixed(2));
    } else {
      setCubicWeight('');
    }
  }, [length, width, height]);

  // Update service type when country changes for cash customers
  React.useEffect(() => {
    if (!isCreditCustomer && countryToData && countryToData !== '1') {
      setServiceType('export');
    }
  }, [countryToData, isCreditCustomer]);

  const loadInitialData = () => {
    // Load destination countries (for both customer types, but credit only uses Nigeria)
    SuperAdminApis.getAllCountryTo('').then((response: AxiosResponse<any>) => {
      if (response?.data) {
        setCountryTo(response?.data);
      }
    });

    // Load origin states (ZoneState - for Nigeria)
    SuperAdminApis.getAllStateFrom('', {}).then((response: AxiosResponse<any>) => {
      if (response?.data) {
        setStateFrom(response?.data);
      }
    });

    // Load destination states (ZoneState)
    SuperAdminApis.getAllStateTo('', {}).then((response: AxiosResponse<any>) => {
      if (response?.data) {
        setStateTo(response?.data);
      }
    });
  };

  const getStateTo = React.useCallback((id: any) => {
    setCountryToData(id);
    setStateToData('');
    setStateToName('');
    setLgaToData('');
    setLgaTo([]);

    // Automatically set service type based on country (cash customers only)
    if (!isCreditCustomer) {
      if (id !== '1') {
        setServiceType('export');
      } else {
        setServiceType('domestic');
      }
    }

    // Load states (ZoneState)
    SuperAdminApis.getAllStateTo('', {}).then((response: AxiosResponse<any>) => {
      if (response?.data) {
        setStateTo(response?.data);
      }
    });
  }, [isCreditCustomer]);

  // Determine LGA service type based on weight
  const getLgaServiceType = (currentWeight: string): string => {
    if (!currentWeight) return 'domestic';
    const weightNum = parseFloat(currentWeight);
    // Domestic: 0.5-20kg, Bulk: 20kg+
    return weightNum >= 20 ? 'bulk' : 'domestic';
  };

  // Get LGA FROM using new getLgasByState endpoint
  const getLgaFrom = React.useCallback((stateId: any, stateName: string) => {
    setStateFromData(stateId);
    setStateFromName(stateName);
    setLgaFromData('');
    setLgaFrom([]);

    if (!stateName || !weight) return;

    const lgaServiceType = getLgaServiceType(weight);

    SuperAdminApis.getLgasByState(stateName, lgaServiceType, isCreditCustomer ? userLoginData?.data?.email : '')
      .then((response: AxiosResponse<any>) => {
        if (response?.data?.status) {
          setLgaFrom(response?.data?.data || []);
        } else {
          setLgaFrom([]);
        }
      })
      .catch((error) => {
        console.error('Error loading LGAs from:', error);
        setLgaFrom([]);
      });
  }, [weight, isCreditCustomer, userLoginData]);

  // Get LGA TO using new getLgasByState endpoint
  const getLgaTo = React.useCallback((stateId: any, stateName: string) => {
    setStateToData(stateId);
    setStateToName(stateName);
    setLgaToData('');
    setLgaTo([]);

    if (!stateName || !weight) return;

    const lgaServiceType = getLgaServiceType(weight);

    SuperAdminApis.getLgasByState(stateName, lgaServiceType, isCreditCustomer ? userLoginData?.data?.email : '')
      .then((response: AxiosResponse<any>) => {
        if (response?.data?.status) {
          setLgaTo(response?.data?.data || []);
        } else {
          setLgaTo([]);
        }
      })
      .catch((error) => {
        console.error('Error loading LGAs to:', error);
        setLgaTo([]);
      });
  }, [weight, isCreditCustomer, userLoginData]);

  // Reload LGAs when weight changes significantly (crosses 20kg threshold)
  React.useEffect(() => {
    if (weight) {
      if (stateFromName) {
        getLgaFrom(stateFromData, stateFromName);
      }
      if (stateToName) {
        getLgaTo(stateToData, stateToName);
      }
    }
  }, [weight]);

  // Helper to display LGA rate info
  const getLgaRateInfo = (lga: any) => {
    if (!lga.onforwarding_value && !lga.add_1kg_value) return '';
    return ` (Base: ₦${parseFloat(lga.onforwarding_value || 0).toLocaleString()}, +₦${parseFloat(lga.add_1kg_value || 0).toLocaleString()}/kg)`;
  };

  const calculate = React.useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoader(true);
    setResult(null);

    if (isCreditCustomer) {
      // CREDIT CUSTOMER CALCULATION
      let data: any = {
        "client_id": userLoginData?.data?.email,
        "shipment_rate_type": shipmentRateType,
        "state_from": stateFromData || null,
        "state_to": stateToData,
        "lga_from": lgaFromData || 'empty',
        "lga_to": lgaToData || 'empty',
        "weight": parseFloat(weight)
      };

      // Add cubic weight if provided and not special rate
      if (shipmentRateType !== 'special' && cubicWeight && parseFloat(cubicWeight) > 0) {
        data.weight = Math.max(parseFloat(weight), parseFloat(cubicWeight));
      }

      // Add special unit type if special rate
      if (shipmentRateType === 'special') {
        if (!specialUnitType) {
          toast.error('Please select unit type for special rates');
          setLoader(false);
          return;
        }
        data.special_unit_type = specialUnitType;
      }

      console.log('Credit Customer Payload:', data); // Debug log

      SuperAdminApis.calculateCreditCustomerRate(data).then((response: any) => {
        setLoader(false);
        if (response?.data?.status) {
          setResult(response?.data?.data);
          toast.success('Rate calculated successfully');
        } else {
          toast.warn(response?.response?.data?.message || 'Rate calculation failed');
        }
      }).catch((error) => {
        setLoader(false);
        toast.error(error?.response?.data?.message || 'An error occurred during calculation');
      });

    } else {
      // CASH CUSTOMER CALCULATION (unchanged)
      let data: any = {
        "country_from": 1,
        "country_to": parseInt(countryToData),
        "state_from": stateFromData || null,
        "state_to": stateToData,
        "lga_from": lgaFromData || 'empty',
        "lga_to": lgaToData || 'empty',
        "weight": parseFloat(weight),
        "service_type": serviceType
      };

      if (cubicWeight && parseFloat(cubicWeight) > 0) {
        data.cubic_weight = parseFloat(cubicWeight);
      }

      console.log('Cash Customer Payload:', data); // Debug log

      SuperAdminApis.calculateRate(data).then((response: any) => {
        setLoader(false);
        if (response?.data?.status) {
          setResult(response?.data?.data);
          toast.success('Rate calculated successfully');
        } else {
          toast.warn(response?.response?.data?.message || 'Rate calculation failed');
        }
      }).catch((error) => {
        setLoader(false);
        toast.error(error?.response?.data?.message || 'An error occurred during calculation');
      });
    }
  }, [isCreditCustomer, shipmentRateType, specialUnitType, countryToData, stateFromData, stateToData, lgaFromData, lgaToData, weight, cubicWeight, serviceType, userLoginData]);
  const getServiceTypeLabel = (type: string) => {
    switch (type) {
      case 'domestic_normal': return 'Domestic (0.5kg - 10kg)';
      case 'domestic_bulk': return 'Bulk (20.5kg - 50kg)';
      case 'domestic_bulk_extra': return 'Bulk Extra (51kg - 500kg)';
      case 'domestic_bulk_gap': return 'Domestic Bulk (10-20kg)';
      case 'express': return 'Express Delivery';
      case 'export': return 'International Export';
      default: return type;
    }
  };

  const getRateTypeLabel = (type: string) => {
    switch (type) {
      case 'Fixed Rate': return 'Fixed Rate (Exact Weight)';
      case 'Bulk Rate': return 'Bulk Rate (Rate × Weight)';
      case 'Flat Rate': return 'Flat Rate (Fixed Price Range)';
      case 'Multiplier Rate': return 'Multiplier Rate (Rate × Weight)';
      case 'Special Rate': return 'Special Rate (Unit-based)';
      default: return type;
    }
  };

  return (
    <>
      <Sidebar title="Calculate Rate" />
      <div className="relative md:ml-64 bg-[#FAFAFA]">
        <AdminNavbar title="Calculate Rate" />

        <div className='lg:px-10 pb-6 px-6 pt-5 bg-[#FAFAFA] min-h-screen'>
          <div className='bg-white border border-[#E2E8F0] rounded-[10px] pt-[60px] pb-[40px] px-[23px]'>

            {/* Customer Type Badge */}
            <div className='mb-6'>
              <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${isCreditCustomer
                ? 'bg-purple-100 text-purple-800'
                : 'bg-blue-100 text-blue-800'
                }`}>
                {isCreditCustomer ? '💼 Credit Customer' : '💵 Cash Customer'}
              </span>
            </div>

            {/* Origin Indicator */}
            <div className='mb-6 bg-green-50 border-l-4 border-green-500 rounded-lg p-4'>
              <div className='flex items-center space-x-3'>
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className='font-semibold text-sm text-green-800'>Origin Country: Nigeria</p>
                  <p className='text-xs text-green-600'>All shipments originate from Nigeria</p>
                </div>
              </div>
            </div>

            <form onSubmit={calculate}>

              {/* RATE TYPE SELECTION - Different for Credit vs Cash */}
              {isCreditCustomer ? (
                // CREDIT CUSTOMER: 5 Rate Types
                <div className='mb-6'>
                  <h3 className='text-[16px] font-semibold mb-3'>Rate Type</h3>
                  <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4'>
                    <label className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${shipmentRateType === 'fixed' ? 'border-purple-600 bg-purple-50' : 'border-gray-200'}`}>
                      <input
                        type="radio"
                        name="shipmentRateType"
                        value="fixed"
                        checked={shipmentRateType === 'fixed'}
                        onChange={(e) => setShipmentRateType(e.target.value)}
                        className="w-4 h-4 text-purple-600"
                      />
                      <div>
                        <p className='font-semibold text-sm'>Fixed</p>
                        <p className='text-xs text-gray-500'>Exact weight</p>
                      </div>
                    </label>

                    <label className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${shipmentRateType === 'bulk' ? 'border-purple-600 bg-purple-50' : 'border-gray-200'}`}>
                      <input
                        type="radio"
                        name="shipmentRateType"
                        value="bulk"
                        checked={shipmentRateType === 'bulk'}
                        onChange={(e) => setShipmentRateType(e.target.value)}
                        className="w-4 h-4 text-purple-600"
                      />
                      <div>
                        <p className='font-semibold text-sm'>Bulk</p>
                        <p className='text-xs text-gray-500'>Rate × Weight</p>
                      </div>
                    </label>

                    <label className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${shipmentRateType === 'flat' ? 'border-purple-600 bg-purple-50' : 'border-gray-200'}`}>
                      <input
                        type="radio"
                        name="shipmentRateType"
                        value="flat"
                        checked={shipmentRateType === 'flat'}
                        onChange={(e) => setShipmentRateType(e.target.value)}
                        className="w-4 h-4 text-purple-600"
                      />
                      <div>
                        <p className='font-semibold text-sm'>Flat</p>
                        <p className='text-xs text-gray-500'>Fixed range</p>
                      </div>
                    </label>

                    <label className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${shipmentRateType === 'multiplier' ? 'border-purple-600 bg-purple-50' : 'border-gray-200'}`}>
                      <input
                        type="radio"
                        name="shipmentRateType"
                        value="multiplier"
                        checked={shipmentRateType === 'multiplier'}
                        onChange={(e) => setShipmentRateType(e.target.value)}
                        className="w-4 h-4 text-purple-600"
                      />
                      <div>
                        <p className='font-semibold text-sm'>Multiplier</p>
                        <p className='text-xs text-gray-500'>Rate × Weight</p>
                      </div>
                    </label>

                    <label className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${shipmentRateType === 'special' ? 'border-purple-600 bg-purple-50' : 'border-gray-200'}`}>
                      <input
                        type="radio"
                        name="shipmentRateType"
                        value="special"
                        checked={shipmentRateType === 'special'}
                        onChange={(e) => setShipmentRateType(e.target.value)}
                        className="w-4 h-4 text-purple-600"
                      />
                      <div>
                        <p className='font-semibold text-sm'>Special</p>
                        <p className='text-xs text-gray-500'>Unit-based</p>
                      </div>
                    </label>
                  </div>

                  {/* Special Unit Type Selection - Only for Special Rate */}
                  {shipmentRateType === 'special' && (
                    <div className='mt-4'>
                      <label className="block mb-2 text-sm font-semibold text-[#19213D]">
                        Unit Type <span className='text-red-400'>*</span>
                      </label>
                      <select
                        value={specialUnitType}
                        onChange={(e) => setSpecialUnitType(e.target.value)}
                        required
                        className="bg-white border-[0.5px] border-[#DADADA] text-[#333333] text-sm rounded-[5px] block w-full md:w-1/3 p-2 cursor-pointer"
                      >
                        <option value="">--Select Unit Type--</option>
                        <option value="Carton">Carton</option>
                        <option value="Keg">Keg</option>
                      </select>
                    </div>
                  )}
                </div>
              ) : (
                // CASH CUSTOMER: Service Type Selection - Only for domestic (Nigeria to Nigeria)
                countryToData === '1' && (
                  <div className='mb-6'>
                    <h3 className='text-[16px] font-semibold mb-3'>Service Type (Domestic)</h3>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      <label className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${serviceType === 'domestic' ? 'border-[#E04343] bg-red-50' : 'border-gray-200'}`}>
                        <input
                          type="radio"
                          name="serviceType"
                          value="domestic"
                          checked={serviceType === 'domestic'}
                          onChange={(e) => setServiceType(e.target.value)}
                          className="w-4 h-4 text-[#E04343]"
                        />
                        <div>
                          <p className='font-semibold text-sm'>Domestic</p>
                          <p className='text-xs text-gray-500'>Standard delivery</p>
                        </div>
                      </label>

                      <label className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${serviceType === 'express' ? 'border-[#E04343] bg-red-50' : 'border-gray-200'}`}>
                        <input
                          type="radio"
                          name="serviceType"
                          value="express"
                          checked={serviceType === 'express'}
                          onChange={(e) => setServiceType(e.target.value)}
                          className="w-4 h-4 text-[#E04343]"
                        />
                        <div>
                          <p className='font-semibold text-sm'>Express</p>
                          <p className='text-xs text-gray-500'>Fast delivery</p>
                        </div>
                      </label>
                    </div>
                  </div>
                )
              )}

              {/* Export Service Indicator - Cash customers only */}
              {!isCreditCustomer && countryToData !== '1' && (
                <div className='mb-6 bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4'>
                  <div className='flex items-center space-x-3'>
                    <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className='font-semibold text-sm text-blue-800'>International Export</p>
                      <p className='text-xs text-blue-600'>Service type automatically set to Export</p>
                    </div>
                  </div>
                </div>
              )}

              {/* LGA Rate Info Banner - For Nigeria destinations */}
              {countryToData === '1' && (
                <div className='mb-6 bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4'>
                  <div className='flex items-start space-x-3'>
                    <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className='font-semibold text-sm text-yellow-800'>LGA Rate System</p>
                      <p className='text-xs text-yellow-700 mt-1'>
                        {isCreditCustomer
                          ? 'Your custom LGA rates will be applied based on your agreement.'
                          : 'LGAs are loaded based on your weight selection:'}
                      </p>
                      {!isCreditCustomer && (
                        <ul className='text-xs text-yellow-700 mt-2 ml-4 list-disc space-y-1'>
                          <li><strong>Weight 0.5-20kg:</strong> Domestic LGA rates</li>
                          <li><strong>Weight 20kg+:</strong> Bulk LGA rates for heavy shipments</li>
                          <li>Enter weight first, then select state to see available LGAs</li>
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* SHIPMENT INFORMATION */}
              <div className='grid md:grid-cols-4 gap-3'>
                <div>
                  <h3 className='text-[16px] font-semibold mt-7'>Shipment Information</h3>
                  <p className='text-xs text-gray-500 mt-1'>Enter weight first to see correct LGAs</p>
                </div>

                <div className="w-full">
                  <div className="relative w-full mb-5">
                    <label className="block mb-2 text-sm font-semibold text-[#19213D]">
                      Dead Weight (KG) <span className='text-red-400'>*</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0.1"
                      className="bg-white border-[0.5px] border-[#DADADA] text-[#333333] text-sm rounded-[5px] block w-full p-2"
                      placeholder="Enter weight"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      required
                    />
                    {weight && (
                      <p className='text-xs text-blue-600 mt-1'>
                        {parseFloat(weight) >= 20
                          ? '📦 Bulk weight - bulk LGA rates will be shown'
                          : '📦 Domestic weight - domestic LGA rates will be shown'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Dimensions - Hide for Credit Special Rate */}
                {!(isCreditCustomer && shipmentRateType === 'special') && (
                  <>
                    <div className="w-full">
                      <div className="relative w-full mb-5">
                        <label className="block mb-2 text-sm font-semibold text-[#19213D]">
                          Length (cm) <span className='text-gray-400 font-thin'>(Optional)</span>
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          className="bg-white border-[0.5px] border-[#DADADA] text-[#333333] text-sm rounded-[5px] block w-full p-2"
                          placeholder="Length"
                          value={length}
                          onChange={(e) => setLength(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="w-full">
                      <div className="relative w-full mb-5">
                        <label className="block mb-2 text-sm font-semibold text-[#19213D]">
                          Width (cm) <span className='text-gray-400 font-thin'>(Optional)</span>
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          className="bg-white border-[0.5px] border-[#DADADA] text-[#333333] text-sm rounded-[5px] block w-full p-2"
                          placeholder="Width"
                          value={width}
                          onChange={(e) => setWidth(e.target.value)}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Height and Cubic Weight - Hide for Credit Special Rate */}
              {!(isCreditCustomer && shipmentRateType === 'special') && (
                <div className='grid md:grid-cols-4 gap-3 mt-1'>
                  <div></div>

                  <div className="w-full">
                    <div className="relative w-full mb-5">
                      <label className="block mb-2 text-sm font-semibold text-[#19213D]">
                        Height (cm) <span className='text-gray-400 font-thin'>(Optional)</span>
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        className="bg-white border-[0.5px] border-[#DADADA] text-[#333333] text-sm rounded-[5px] block w-full p-2"
                        placeholder="Height"
                        value={height}
                        onChange={(e) => setHeight(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="w-full">
                    <div className="relative w-full mb-5">
                      <label className="block mb-2 text-sm font-semibold text-[#19213D]">
                        Cubic Weight (KG)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        className="bg-gray-100 border-[0.5px] border-[#DADADA] text-[#333333] text-sm rounded-[5px] block w-full p-2"
                        placeholder="Auto-calculated"
                        value={cubicWeight}
                        readOnly
                      />
                      <p className='text-xs text-gray-500 mt-1'>L × W × H ÷ 5000</p>
                    </div>
                  </div>

                  <div></div>
                </div>
              )}

              {/* SOURCE INFORMATION */}
              <div className='grid md:grid-cols-4 gap-3 mt-5'>
                <div>
                  <h3 className='text-[16px] font-semibold mt-7'>Source Information</h3>
                  <p className='text-xs text-gray-500 mt-1'>
                    {isCreditCustomer ? 'Optional - for reference only' : 'Optional - for LGA charges'}
                  </p>
                </div>

                <div className="w-full">
                  <div className="relative w-full mb-5">
                    <label className="block mb-2 text-sm font-semibold text-[#19213D]">
                      State <span className='text-gray-400 font-thin'>(Optional)</span>
                    </label>
                    <select
                      onChange={(e) => {
                        const selectedState = stateFrom?.data?.find((s: any) => s.id === parseInt(e.target.value));
                        if (selectedState) {
                          getLgaFrom(e.target.value, selectedState.state_name);
                        }
                      }}
                      className="bg-white border-[0.5px] border-[#DADADA] text-[#333333] text-sm rounded-[5px] block w-full p-2"
                      disabled={!weight}
                    >
                      <option value="">--Select State--</option>
                      {stateFrom?.data?.map((datas: any) => (
                        <option key={datas?.id} value={datas?.id}>
                          {datas?.state_name} {datas?.zone_number ? `(Zone ${datas?.zone_number})` : ''}
                        </option>
                      ))}
                    </select>
                    {!weight && (
                      <p className='text-xs text-orange-500 mt-1'>⚠️ Enter weight first</p>
                    )}
                  </div>
                </div>

                <div className="w-full">
                  <div className="relative w-full mb-5">
                    <label className="block mb-2 text-sm font-semibold text-[#19213D]">
                      LGA <span className='text-gray-400 font-thin'>(Optional)</span>
                    </label>
                    <select
                      onChange={(e) => setLgaFromData(e.target.value)}
                      value={lgaFromData}
                      className="bg-white border-[0.5px] border-[#DADADA] text-[#333333] text-sm rounded-[5px] block w-full p-2"
                      disabled={!stateFromData || lgaFrom.length === 0}
                    >
                      <option value="">--Select LGA--</option>
                      {lgaFrom.map((lga: any, index: number) => (
                        <option key={index} value={lga?.lga_name}>
                          {lga?.lga_name}{getLgaRateInfo(lga)}
                        </option>
                      ))}
                    </select>
                    {stateFromData && lgaFrom.length === 0 && (
                      <p className='text-xs text-gray-500 mt-1'>No LGAs for this weight/state combo</p>
                    )}
                  </div>
                </div>

                <div></div>
              </div>

              {/* DESTINATION INFORMATION */}
              <div className='grid md:grid-cols-4 gap-3 mt-5 md:mt-1'>
                <div>
                  <h3 className='text-[16px] font-semibold mt-7'>
                    Destination Information
                  </h3>
                  <p className='text-xs text-gray-500 mt-1'>Required for rate calculation</p>
                </div>

                {/* Country */}
                <div className="w-full">
                  <div className="relative w-full mb-5">
                    <label className="block mb-2 text-sm font-semibold text-[#19213D]">
                      Country <span className='text-red-400'>*</span>
                    </label>
                    <select
                      onChange={(e) => getStateTo(e.target.value)}
                      value={countryToData}
                      required
                      disabled={isCreditCustomer} // Credit customers only ship to Nigeria
                      className="bg-white border-[0.5px] border-[#DADADA] text-[#333333] text-sm rounded-[5px] block w-full p-2 cursor-pointer disabled:bg-gray-100"
                    >
                      <option value="">--Select Country--</option>
                      <option value="1">Nigeria (Domestic)</option>
                      {!isCreditCustomer && countryTo?.data?.filter((data: any) => data?.id !== 1)?.map((datas: any) => (
                        <option key={datas?.id} value={datas?.id}>{datas?.country_name}</option>
                      ))}
                    </select>
                    {isCreditCustomer && (
                      <p className='text-xs text-purple-600 mt-1'>Credit customers ship within Nigeria only</p>
                    )}
                  </div>
                </div>

                {/* State - Required for All */}
                <div className="w-full">
                  <div className="relative w-full mb-5">
                    <label className="block mb-2 text-sm font-semibold text-[#19213D]">
                      State <span className='text-red-400'>*</span>
                    </label>
                    <select
                      required
                      onChange={(e) => {
                        const selectedState = stateTo?.data?.find((s: any) => s.id === parseInt(e.target.value));
                        if (selectedState) {
                          getLgaTo(e.target.value, selectedState.state_name);
                        }
                      }}
                      className="bg-white border-[0.5px] border-[#DADADA] text-[#333333] text-sm rounded-[5px] block w-full p-2"
                      disabled={!weight}
                    >
                      <option value="">--Select State--</option>
                      {stateTo?.data?.map((datas: any) => (
                        <option key={datas?.id} value={datas?.id}>
                          {datas?.state_name} {datas?.zone_number ? `(Zone ${datas?.zone_number})` : ''}
                        </option>
                      ))}
                    </select>
                    {!weight && (
                      <p className='text-xs text-orange-500 mt-1'>⚠️ Enter weight first</p>
                    )}
                  </div>
                </div>

                {/* LGA - For Both Customer Types */}
                <div className="w-full">
                  <div className="relative w-full mb-5">
                    <label className="block mb-2 text-sm font-semibold text-[#19213D]">
                      LGA <span className='text-gray-400 font-thin'>(Optional)</span>
                    </label>
                    <select
                      onChange={(e) => setLgaToData(e.target.value)}
                      value={lgaToData}
                      className="bg-white border-[0.5px] border-[#DADADA] text-[#333333] text-sm rounded-[5px] block w-full p-2"
                      disabled={!stateToData || lgaTo.length === 0}
                    >
                      <option value="">--Select LGA--</option>
                      {lgaTo.map((lga: any, index: number) => (
                        <option key={index} value={lga?.lga_name}>
                          {lga?.lga_name}{getLgaRateInfo(lga)}
                        </option>
                      ))}
                    </select>
                    {stateToData && lgaTo.length === 0 && (
                      <p className='text-xs text-gray-500 mt-1'>
                        {isCreditCustomer
                          ? 'No custom LGA rates configured for this state'
                          : 'No LGAs for this weight/state combo'}
                      </p>
                    )}
                    {!isCreditCustomer && lgaTo.length > 0 && (
                      <p className='text-xs text-blue-600 mt-1'>
                        {isCreditCustomer ? 'Your custom LGA rates apply' : 'Select LGA to add location charges'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="w-full mt-4">
                <button
                  type='submit'
                  disabled={loader}
                  className={`${isCreditCustomer ? 'bg-purple-600 hover:bg-purple-700' : 'bg-[#E04343] hover:bg-red-600'
                    } text-white py-[10px] rounded-[42px] w-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                >
                  <div className="flex justify-center gap-3">
                    <span>Calculate Rate</span>
                    <Oval
                      visible={loader}
                      height="20"
                      width="20"
                      color="#ffffff"
                      secondaryColor="#FCBF94"
                      ariaLabel="oval-loading"
                    />
                  </div>
                </button>
              </div>
            </form>
          </div>

          {/* RESULTS SUMMARY */}
          {result && (
            <div className='bg-white border border-[#E2E8F0] rounded-[10px] pt-[20px] pb-[10px] px-[23px] mt-[20px]'>
              <div className='flex justify-between items-center mb-2'>
                <h3 className='text-[16px] text-[#263238] font-medium'>Rate Summary</h3>
                <span className={`text-xs px-3 py-1 rounded-full font-semibold ${isCreditCustomer
                  ? 'bg-purple-100 text-purple-800'
                  : 'bg-blue-100 text-blue-800'
                  }`}>
                  {isCreditCustomer
                    ? getRateTypeLabel(result?.breakdown?.rate_type || shipmentRateType)
                    : getServiceTypeLabel(result?.service_type)
                  }
                </span>
              </div>
              <p className='mt-1 text-[13px] text-[#808080]'>
                The total cost includes base rate, additional charges, and 7.5% VAT
              </p>
              <hr className='mt-2 mb-3' />

              {/* CREDIT CUSTOMER RESULTS */}
              {isCreditCustomer && result?.breakdown && (
                <>
                  <div className='flex justify-between mb-2'>
                    <h3 className='text-[#808080] text-[14px]'>Zone</h3>
                    <h4 className='text-[#808080] text-[14px] font-semibold'>Zone {result.breakdown.zone}</h4>
                  </div>

                  {result.breakdown.weight && (
                    <div className='flex justify-between mb-2'>
                      <h3 className='text-[#808080] text-[14px]'>Weight</h3>
                      <h4 className='text-[#808080] text-[14px] font-semibold'>{result.breakdown.weight} kg</h4>
                    </div>
                  )}

                  {result.breakdown.unit_type && (
                    <div className='flex justify-between mb-2'>
                      <h3 className='text-[#808080] text-[14px]'>Unit Type</h3>
                      <h4 className='text-[#808080] text-[14px] font-semibold'>{result.breakdown.unit_type}</h4>
                    </div>
                  )}

                  {result.breakdown.rate_per_kg && (
                    <div className='flex justify-between mb-2'>
                      <h3 className='text-[#808080] text-[14px]'>Rate per KG</h3>
                      <h4 className='text-[#808080] text-[14px]'>
                        {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(result.breakdown.rate_per_kg)}
                      </h4>
                    </div>
                  )}

                  {result.breakdown.base_cost && (
                    <div className='flex justify-between mb-2'>
                      <h3 className='text-[#808080] text-[14px]'>Base Cost</h3>
                      <h4 className='text-[#808080] text-[14px]'>
                        {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(result.breakdown.base_cost)}
                      </h4>
                    </div>
                  )}

                  {result.breakdown.flat_rate && (
                    <div className='flex justify-between mb-2'>
                      <h3 className='text-[#808080] text-[14px]'>Flat Rate</h3>
                      <h4 className='text-[#808080] text-[14px]'>
                        {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(result.breakdown.flat_rate)}
                      </h4>
                    </div>
                  )}

                  {result.breakdown.unit_rate && (
                    <div className='flex justify-between mb-2'>
                      <h3 className='text-[#808080] text-[14px]'>Unit Rate</h3>
                      <h4 className='text-[#808080] text-[14px]'>
                        {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(result.breakdown.unit_rate)}
                      </h4>
                    </div>
                  )}

                  {/* LGA Charges for Credit Customers */}
                  {(result?.lga_charges > 0 || result.breakdown?.lga_charges > 0) && (
                    <div className='bg-purple-50 border border-purple-200 rounded-lg p-3 mb-2 mt-3'>
                      <div className='flex justify-between mb-1'>
                        <h3 className='text-purple-800 text-[14px] font-semibold'>LGA Charges</h3>
                        <h4 className='text-purple-800 text-[14px] font-bold'>
                          {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(result?.lga_charges || result.breakdown?.lga_charges || 0)}
                        </h4>
                      </div>
                      <p className='text-xs text-purple-700 mt-1'>
                        Your custom LGA rates: onforwarding value + (extra weight × per-kg rate)
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* CASH CUSTOMER RESULTS */}
              {!isCreditCustomer && (
                <>
                  {result?.zone && (
                    <div className='flex justify-between mb-2'>
                      <h3 className='text-[#808080] text-[14px]'>Zone</h3>
                      <h4 className='text-[#808080] text-[14px] font-semibold'>Zone {result.zone}</h4>
                    </div>
                  )}

                  {result?.category && (
                    <div className='flex justify-between mb-2'>
                      <h3 className='text-[#808080] text-[14px]'>Delivery Category</h3>
                      <h4 className='text-[#808080] text-[14px] font-semibold'>{result.category}</h4>
                    </div>
                  )}

                  {result?.country && (
                    <div className='flex justify-between mb-2'>
                      <h3 className='text-[#808080] text-[14px]'>Destination Country</h3>
                      <h4 className='text-[#808080] text-[14px] font-semibold'>{result.country}</h4>
                    </div>
                  )}

                  {result?.actual_weight && (
                    <div className='flex justify-between mb-2'>
                      <h3 className='text-[#808080] text-[14px]'>Chargeable Weight</h3>
                      <h4 className='text-[#808080] text-[14px] font-semibold'>{result.actual_weight} kg</h4>
                    </div>
                  )}

                  {result?.base_cost !== undefined && (
                    <div className='flex justify-between mb-2'>
                      <h3 className='text-[#808080] text-[14px]'>Base Cost</h3>
                      <h4 className='text-[#808080] text-[14px]'>
                        {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(result?.base_cost || 0)}
                      </h4>
                    </div>
                  )}

                  {result?.additional_cost > 0 && (
                    <div className='flex justify-between mb-2'>
                      <h3 className='text-[#808080] text-[14px]'>Additional Cost</h3>
                      <h4 className='text-[#808080] text-[14px]'>
                        {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(result.additional_cost)}
                      </h4>
                    </div>
                  )}

                  {/* LGA Charges for Cash Customers */}
                  {result?.lga_charges > 0 && (
                    <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-2 mt-3'>
                      <div className='flex justify-between mb-1'>
                        <h3 className='text-[#856404] text-[14px] font-semibold'>LGA Charges</h3>
                        <h4 className='text-[#856404] text-[14px] font-bold'>
                          {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(result.lga_charges)}
                        </h4>
                      </div>
                      <p className='text-xs text-yellow-700 mt-1'>
                        Location-based charges: onforwarding value + (extra weight × per-kg rate)
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* COMMON RESULTS FOR BOTH */}
              <div className='flex justify-between mb-2 mt-3'>
                <h3 className='text-[#808080] text-[14px]'>Subtotal</h3>
                <h4 className='text-[#808080] text-[14px]'>
                  {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(result?.subtotal || 0)}
                </h4>
              </div>

              <div className='flex justify-between mb-2'>
                <h3 className='text-[#808080] text-[14px]'>VAT (7.5%)</h3>
                <h4 className='text-[#808080] text-[14px]'>
                  {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(result?.vat || result?.tax || 0)}
                </h4>
              </div>

              <hr className='mt-4 mb-3' />

              <div className='flex justify-between mb-2'>
                <h3 className={`text-[16px] font-semibold ${isCreditCustomer ? 'text-purple-600' : 'text-[#E04343]'}`}>
                  Total Cost
                </h3>
                <h4 className={`text-[24px] font-bold ${isCreditCustomer ? 'text-purple-600' : 'text-[#E04343]'}`}>
                  {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(result?.total_cost || 0)}
                </h4>
              </div>

              {/* Rate Calculation Note */}
              <div className='mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3'>
                <p className='text-xs text-gray-600'>
                  <strong>Note:</strong> {isCreditCustomer
                    ? `This rate is calculated based on your custom rate agreement with the destination zone.${(result?.lga_charges > 0 || result?.breakdown?.lga_charges > 0) ? ' Your custom LGA charges are included based on your agreement.' : ''}`
                    : `This rate is calculated based on the destination zone, service type, and weight.${result?.lga_charges > 0 ? ' LGA charges are calculated from weight ranges with onforwarding value and per-kg rates.' : ''}`
                  }
                </p>
              </div>

              {/* Breakdown Details - Optional detailed view */}
              {(result?.lga_charges > 0 || result?.breakdown?.lga_charges > 0) && (
                <div className='mt-3 bg-blue-50 border-l-4 border-blue-500 rounded-lg p-3'>
                  <div className='flex items-start space-x-2'>
                    <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className='font-semibold text-sm text-blue-800'>LGA Charges Breakdown</p>
                      <p className='text-xs text-blue-700 mt-1'>
                        {isCreditCustomer
                          ? 'LGA charges are based on your custom rates for the selected locations.'
                          : `LGA charges apply when shipping to/from specific local government areas. Charges include a base onforwarding fee plus per-kilogram rates for weight exceeding the base weight threshold.`
                        }
                      </p>
                      {lgaFromData && lgaFromData !== 'empty' && (
                        <p className='text-xs text-blue-600 mt-1'>
                          <strong>Origin LGA:</strong> {lgaFromData}
                        </p>
                      )}
                      {lgaToData && lgaToData !== 'empty' && (
                        <p className='text-xs text-blue-600 mt-1'>
                          <strong>Destination LGA:</strong> {lgaToData}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}